import { FGMultiSuggestInput, FGTextInput, FieldGroup, useFGContext } from "nsitools-react";
import * as React from "react";

import { ContratBaremeList, HeuresHebdomadairesSelector } from "..";
import { nameof } from "../../../../../../utils";
import { EStatutContrat, ETypeContrat, SiegeSocialEditDto } from "../../../../../../api";
import { ERoutes } from "../../../../../../AppRouter";
import { FGWalterDateMaskInput, FGWalterSelectInput, WarningText } from "../../../../../../components";
import { useTl } from "../../../../../../hooks";
import { useReferential } from "../../../../../../hooks/useReferential";
import { ETLCodes } from "../../../../../../locales";

export interface IContratAlternancePanelProps {
  typeContrat: ETypeContrat;
  idsiegeSocial: number;
  readonly?: boolean;
}

export const ContratAlternancePanel: React.FunctionComponent<IContratAlternancePanelProps> = ({
  typeContrat,
  idsiegeSocial,
  readonly = false
}) => {
  const { t } = useTl();
  const { formik } = useFGContext();

  const [dureesContrat, dccLoading] = useReferential(
    a => a.referentialGetDureesContrat({ idstade: +(formik?.values?.idstade ?? 0), typeContrat }),
    false,
    [typeContrat, formik?.values?.idstade]
  );

  const [metiers, mLoading] = useReferential(
    a =>
      a.referentialMetierByStadeForContrat({
        idStade: +(formik?.values?.idstade ?? 0),
        currentId: +(formik?.values?.idmetier ?? 0)
      }),
    false,
    [formik?.values?.idstade, formik?.values?.idmetier]
  );

  const [commissionsParitaires, cpLoading] = useReferential(
    a =>
      a.referentialGetCommissionsParitaires({
        idmetier: 0,
        idsiegeSocial,
        currentId: formik.values?.idcommissionParitaire ?? 0
      }),
    false,
    [idsiegeSocial, formik.values?.idcommissionParitaire]
  );

  const [clauses, cLoading] = useReferential(a => a.referentialGetClausesContrat(), true);

  const [lieuxSignature, lsLoading] = useReferential(a => a.referentialGetLieuxSignature());

  const softRO = React.useMemo(() => [EStatutContrat.Rompu].includes(formik?.values?.statut), [formik?.values?.statut]);

  return (
    <>
      <FieldGroup
        columns={2}
        collapsable
        fieldsetProps={{
          title: t(ETLCodes.Detail)
        }}
      >
        <FieldGroup>
          <FGTextInput name="stade" label={t(ETLCodes.Stade)} readonly />
          <FGWalterSelectInput
            name="iddureeContrat"
            label={t(ETLCodes.DureeParcours)}
            items={dureesContrat}
            loading={dccLoading}
            readonly={softRO || readonly}
          />
          <FGWalterSelectInput
            name="idmetier"
            label={t(ETLCodes.Metier)}
            items={metiers}
            loading={mLoading}
            readonly={formik?.values?.idcontrat > 0 || softRO || readonly}
          />
          <FGWalterSelectInput
            name="idcommissionParitaire"
            label={t(ETLCodes.CommissionParitaire)}
            items={commissionsParitaires}
            loading={cpLoading}
            readonly={softRO || readonly}
            helperText={ctx =>
              !!idsiegeSocial &&
              commissionsParitaires?.length === 0 && <WarningText text={t(ETLCodes.AucuneCpDansCeSiegeSocial)} />
            }
            itemCreateUrl={`${ERoutes.siegeSocial}/${idsiegeSocial}/detail/edit`}
            itemCreateParams={`&fieldName=${nameof<SiegeSocialEditDto>("idsCommissionParitaire")}`}
            autoSelectIfOne
          />
          <HeuresHebdomadairesSelector softRO={softRO || readonly} />
          <FGMultiSuggestInput
            name="idsclause"
            label={t(ETLCodes.Clauses)}
            items={clauses}
            loading={cLoading}
            displayField="label"
            valueField="value"
            readonly={softRO || readonly}
          />
        </FieldGroup>
      </FieldGroup>
      <FieldGroup
        columns={2}
        collapsable
        fieldsetProps={{
          title: t(ETLCodes.LieuxDates)
        }}
      >
        <FieldGroup>
          <FGWalterDateMaskInput name="dateDebut" label={t(ETLCodes.DateDebut)} readonly={softRO || readonly} />
          <FGWalterDateMaskInput name="dateFin" label={t(ETLCodes.DateFin)} readonly={softRO || readonly} />
          <FGWalterDateMaskInput name="dateSignature" label={t(ETLCodes.DateSignature)} readonly={softRO || readonly} />
          <FGWalterSelectInput
            name="idlieuSignature"
            label={t(ETLCodes.LieuSignature)}
            items={lieuxSignature}
            loading={lsLoading}
            readonly={softRO || readonly}
          />
          <FGWalterDateMaskInput
            name="contratAlternanceDto.datePassageB"
            label={t(ETLCodes.DatePassageB)}
            readonly={softRO || readonly}
          />
          <FGWalterDateMaskInput
            name="contratAlternanceDto.datePassageC"
            label={t(ETLCodes.DatePassageC)}
            readonly={softRO || readonly}
          />
          <ContratBaremeList typeContrat={typeContrat} readonly={readonly} />
        </FieldGroup>
      </FieldGroup>
    </>
  );
};
